/**
 * PendingSessionService - Lightweight API service for landing pages
 *
 * Handles creating/updating pending sessions without the full App context.
 * Used by landing pages to collect JD and resumes before sending users to recruiter app.
 *
 * All data is in-memory only - synced to backend via syncToBackend() before redirect.
 */
import { Config } from '../../config';
import { IndustryContext, } from '@common/clientServerApi';
// ============================================================================
// Service Class
// ============================================================================
export class PendingSessionService {
    constructor() {
        this.sessionId = null;
        this.files = [];
        this.jobDescription = null;
        this.candidates = [];
        this.industryContext = IndustryContext.Logistics;
        this.upgradePlan = null;
        this.config = new Config();
        this.upgradePlan = this.readUpgradePlanFromUrl();
    }
    // ========================================================================
    // Utility
    // ========================================================================
    generateSessionId() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    readUpgradePlanFromUrl() {
        if (typeof window === 'undefined') {
            return null;
        }
        const params = new URLSearchParams(window.location.search);
        const upgrade = params.get('upgrade');
        if (upgrade === 'starter' || upgrade === 'advanced') {
            return upgrade;
        }
        return null;
    }
    // ========================================================================
    // Session Management
    // ========================================================================
    /**
     * Get or create a session ID
     */
    getSessionId() {
        if (!this.sessionId) {
            this.sessionId = this.generateSessionId();
        }
        return this.sessionId;
    }
    /**
     * Get current session data
     */
    getSessionData() {
        return {
            sessionId: this.getSessionId(),
            files: [...this.files],
            jobDescription: this.jobDescription,
            candidates: [...this.candidates],
            industryContext: this.industryContext,
        };
    }
    /**
     * Check if session has data worth converting
     */
    hasData() {
        return this.files.length > 0 || this.jobDescription !== null || this.candidates.length > 0;
    }
    /**
     * Clear all session data
     */
    clearSession() {
        this.sessionId = null;
        this.files = [];
        this.jobDescription = null;
        this.candidates = [];
        this.industryContext = IndustryContext.Logistics;
    }
    /**
     * Start a completely fresh session with a new ID.
     * Clears all in-memory state and generates a new session ID.
     */
    startFreshSession() {
        this.files = [];
        this.jobDescription = null;
        this.candidates = [];
        this.industryContext = IndustryContext.Logistics;
        this.sessionId = this.generateSessionId();
        console.log('[PendingSessionService] Started fresh session:', this.sessionId);
        return this.sessionId;
    }
    // ========================================================================
    // File Management
    // ========================================================================
    /**
     * Add files to pending list (in-memory only, not uploaded yet)
     */
    addFiles(newFiles) {
        const added = [];
        for (const file of newFiles) {
            const info = {
                id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                file,
                name: file.name,
                size: file.size,
                status: 'pending',
            };
            this.files.push(info);
            added.push(info);
        }
        return added;
    }
    /**
     * Remove a file from pending list
     */
    removeFile(fileId) {
        this.files = this.files.filter(f => f.id !== fileId);
    }
    /**
     * Get all pending files
     */
    getFiles() {
        return [...this.files];
    }
    /**
     * Clear all files
     */
    clearFiles() {
        this.files = [];
    }
    // ========================================================================
    // Job Description Management
    // ========================================================================
    /**
     * Set job description (in-memory)
     */
    setJobDescription(jd) {
        this.jobDescription = jd;
    }
    /**
     * Get job description
     */
    getJobDescription() {
        return this.jobDescription;
    }
    /**
     * Clear job description
     */
    clearJobDescription() {
        this.jobDescription = null;
    }
    // ========================================================================
    // Candidate Templates (pre-parsed candidates, no resume file)
    // ========================================================================
    /** Add a pre-parsed candidate (e.g. sample template) to the session. */
    addCandidate(candidate) {
        this.candidates.push(candidate);
    }
    /** Replace all candidates with the given list. */
    setCandidates(candidates) {
        this.candidates = [...candidates];
    }
    /** Clear all candidate templates. */
    clearCandidates() {
        this.candidates = [];
    }
    /** Get candidate templates. */
    getCandidates() {
        return [...this.candidates];
    }
    // ========================================================================
    // Industry Context
    // ========================================================================
    /**
     * Set industry context
     */
    setIndustryContext(industry) {
        this.industryContext = industry;
    }
    /**
     * Get industry context
     */
    getIndustryContext() {
        return this.industryContext;
    }
    // ========================================================================
    // API Methods - Save to Backend
    // ========================================================================
    /**
     * Sync all pending session data to backend in a SINGLE request
     * This is more efficient than multiple calls - one DB write, one request
     */
    async syncToBackend() {
        console.log('[PendingSessionService] syncToBackend starting (single request)');
        console.log('[PendingSessionService] files count:', this.files.length);
        console.log('[PendingSessionService] jobDescription:', this.jobDescription?.title);
        try {
            const sessionId = this.getSessionId();
            // Convert all files to base64
            const filesData = [];
            for (const fileInfo of this.files) {
                if (fileInfo.status === 'pending') {
                    try {
                        const base64 = await this.fileToBase64(fileInfo.file);
                        filesData.push({
                            fileName: fileInfo.name,
                            fileBase64: base64,
                            contentType: fileInfo.file.type || 'application/pdf',
                        });
                        fileInfo.status = 'uploading';
                    }
                    catch (e) {
                        console.error('[PendingSessionService] Failed to read file:', fileInfo.name, e);
                    }
                }
            }
            // Single request with all data
            const response = await fetch(`${this.config.apiUrl}/public/pending-session/sync`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessionId,
                    files: filesData,
                    jobDescription: this.jobDescription,
                    candidates: this.candidates.length > 0 ? this.candidates : undefined,
                    industryContext: this.industryContext,
                    referrer: window.location.pathname,
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Sync failed: ${response.status} ${errorText}`);
            }
            const result = await response.json();
            // Mark all files as uploaded
            for (const fileInfo of this.files) {
                if (fileInfo.status === 'uploading') {
                    fileInfo.status = 'uploaded';
                }
            }
            console.log('[PendingSessionService] syncToBackend complete, success:', result.success);
            return {
                success: result.success,
                errors: result.errors || [],
            };
        }
        catch (e) {
            const errorMsg = e instanceof Error ? e.message : String(e);
            console.error('[PendingSessionService] syncToBackend failed:', e);
            return {
                success: false,
                errors: [errorMsg],
            };
        }
    }
    // ========================================================================
    // Utility Methods
    // ========================================================================
    /**
     * Build URL to recruiter app with session ID
     */
    getTargetAppUrl() {
        const sessionId = this.getSessionId();
        const params = new URLSearchParams({ session: sessionId });
        if (this.upgradePlan) {
            params.set('upgrade', this.upgradePlan);
        }
        return `/recruiter?${params.toString()}`;
    }
    /**
     * Convert File to base64 string
     */
    fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result;
                // Remove data URL prefix (data:application/pdf;base64,)
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }
}
// ============================================================================
// Singleton Instance
// ============================================================================
let serviceInstance = null;
export const getPendingSessionService = () => {
    if (!serviceInstance) {
        serviceInstance = new PendingSessionService();
    }
    return serviceInstance;
};
